import { BodyRegular, FlexColMd, Headline2 } from 'components/Elements';
import useGetRequest from 'hooks/useGetRequest';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { theme, sizes } from 'theme';
import Button from 'components/buttons/Button';
import { LearnerLearningObjective } from 'models/LearnerCourse';
import { PublicCourseOverview, PublicModuleOverviews } from 'models/PublicCourseOverview';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import strings from '../../strings/strings.json';
import React from 'react';
import { CopyLinkModal, CopyLinkModalProps, CopyLinkModalState } from 'components/modals/CopyLinkModal';
import { AuthContext } from 'contextProviders/AuthContext';
import Alert from 'components/alert/Alert';
import { PaddedCard } from 'pages/learner/sharedStyles/LearnerStyles';
import Header from 'components/sections/Header';
import DateSection from 'components/courseSchedule/DateSection';
import TrainerAndOrg from './publicCoursePageComponents/TrainerAndOrg';
import {
    CourseCancelledMessage,
    CourseFullMessage,
    CourseNotFoundError,
} from './publicCoursePageComponents/CourseStatusMessages';
import LearningObjectivesCard from './publicCoursePageComponents/LearningObjectivesCard';
import ModulesList from './publicCoursePageComponents/ModulesList';
import BottomButtonSection from './publicCoursePageComponents/BottomButtonSection';
import ArrowRightIcon from 'assets/icons/controls/ArrowRightIcon';
import ShareIcon from 'assets/icons/controls/ShareIcon';

export default function PublicCoursePage(): JSX.Element {
    const { id } = useParams();
    const { isAuthenticated, setState } = React.useContext(AuthContext);
    const navigate = useNavigate();

    const [enrollAlertOpen, setEnrollAlertOpen] = React.useState<boolean>(false);
    const [copyLinkState, setCopyLinkState] = React.useState<CopyLinkModalState>({
        modalProps: {} as CopyLinkModalProps,
        modalOpen: false,
    });

    const {
        data: course,
        loading,
        errors,
        called,
    } = useGetRequest<PublicCourseOverview>(`public/course/${id}`, undefined, true);

    function closeCancelAlert() {
        setEnrollAlertOpen(false);
    }

    function openCopyModal(url: string) {
        setCopyLinkState({
            modalOpen: true,
            modalProps: {
                title: `${strings.copyLinkModal.shareLink.title}`,
                inputValue: url,
                primaryButtonTitle: `${strings.copyLinkModal.shareLink.primaryButtonTitle}`,
                primaryButtonAria: `${strings.copyLinkModal.shareLink.primaryButtonAria}`,
                closeButtonAria: `${strings.copyLinkModal.shareLink.closeButtonAria}`,
                onClickCloseButton: () => {},
            },
        });
    }

    function closeCopyModal() {
        setCopyLinkState({
            modalOpen: false,
            modalProps: {} as CopyLinkModalProps,
        });
    }

    if (!loading && !called) return <></>;
    if (loading) return <></>;
    if (!course || (errors && errors.length > 0)) {
        return <CourseNotFoundError />;
    }

    let endDate;
    let courseEnded = false;

    if (course.endDate) {
        endDate = new Date(course.endDate);
        courseEnded = endDate < new Date();
    }

    const courseFull = course.currentLearners >= course.maximumLearners;
    const learningObjectives: LearnerLearningObjective[] = course.learningObjectives;
    const modules: PublicModuleOverviews[] = course.modules;
    const profileImageFilepath = course.trainer.profileImage ? `${DOCUMENTS_URL}/${course.trainer.profileImage}` : '';

    const shareCourseButton = (
        <Button
            title={strings.publicCoursePage.shareCourseButton.title}
            aria={`${strings.publicCoursePage.shareCourseButton.aria}: ${course.title}`}
            icon={<ShareIcon />}
            onClick={() => {
                const copyLinkUrl = `${window.location.origin}/public/course/${course.scheduledCourseId}`;
                openCopyModal(copyLinkUrl);
            }}
            disabled={courseFull}
        />
    );

    const joinCourseButton = (
        <Button
            title={strings.publicCoursePage.joinCourseButton.title}
            aria={`${strings.publicCoursePage.joinCourseButton.aria}: ${course.title}`}
            colour="PRIMARY"
            disabled={course.cancelled || courseFull || courseEnded}
            iconOnRight
            icon={<ArrowRightIcon />}
            onClick={
                isAuthenticated
                    ? () => setEnrollAlertOpen(true)
                    : () => {
                          setState(`/learner/enroll/${id}`);
                          navigate(`/signup`);
                      }
            }
        />
    );

    const enrollAlert = (
        <Alert
            title={strings.publicCoursePage.joinCourseConfirmation.title}
            alertText={`${strings.publicCoursePage.joinCourseConfirmation.alertText} ${course.title}`}
            buttonText={strings.publicCoursePage.joinCourseConfirmation.buttonText}
            ariaButtonText={`${strings.publicCoursePage.joinCourseConfirmation.buttonAria} ${course.title}`}
            buttonColour={'PRIMARY'}
            onSubmit={() => navigate(`/learner/enroll/${id}`)}
            closeAlert={closeCancelAlert}
        />
    );

    const copyLinkModal = <CopyLinkModal {...copyLinkState.modalProps} onClickCloseButton={closeCopyModal} />;

    return (
        <>
            <Header logoS3Filepath={course.organisation.logoS3Filepath} title={course.title} />

            {course.cancelled && <CourseCancelledMessage />}
            {courseFull && <CourseFullMessage />}

            {copyLinkState.modalOpen && copyLinkModal}
            {enrollAlertOpen && enrollAlert}

            <PublicPage>
                {!course.cancelled && (
                    <ContentBlock>
                        <DateSection
                            startDate={course.startDate}
                            endDate={course.endDate!}
                            currentLearners={course.currentLearners}
                            maximumLearners={course.maximumLearners}
                            shareCourseButton={shareCourseButton}
                            joinCourseButton={joinCourseButton}
                        />
                    </ContentBlock>
                )}

                <ContentBlock>
                    <Headline2>{strings.publicCoursePage.trainerHeadline}</Headline2>
                    <TrainerAndOrg
                        orgName={course.organisation.name}
                        orgPublicName={course.organisation.publicName}
                        orgDescription={course.organisation.description}
                        trainerFirstName={course.trainer.firstName}
                        trainerLastName={course.trainer.lastName}
                        trainerBio={course.trainer.bio}
                        trainerProfileImageFilepath={profileImageFilepath}
                    />
                </ContentBlock>

                {course.description && (
                    <ContentBlock>
                        <Headline2>{strings.publicCoursePage.courseDescriptionHeadline}</Headline2>
                        <PaddedCard>
                            <BodyRegular>{course.description}</BodyRegular>
                        </PaddedCard>
                    </ContentBlock>
                )}

                <ContentBlock>
                    <Headline2>{strings.publicCoursePage.learningObjectivesHeadline}</Headline2>
                    <LearningObjectivesCard learningObjectives={learningObjectives} />
                </ContentBlock>

                <ContentBlock>
                    <Headline2>{strings.publicCoursePage.modulesHeadline}</Headline2>
                    <ModulesList modules={modules} />
                </ContentBlock>

                <BottomButtonSection joinCourseButton={joinCourseButton} />
            </PublicPage>
        </>
    );
}

export const PublicPage = styled.main`
    margin: 0 auto;
    padding: ${sizes.spacingMd} ${sizes.edgePadding};
    width: 100%;
    max-width: ${sizes.appMaxWidthWithoutPadding}px;
    display: flex;
    flex-direction: column;
    background: inherit;
`;

export const ContentBlock = styled(FlexColMd)`
    padding: ${sizes.spacingMd} 0 ${sizes.spacingLg} 0;
    border-bottom: 1px solid ${theme.secondaryButtonDisabledBackgroundColour};
`;
